/** @format — MediaRecorder voice-note lifecycle */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  isVoiceNoteSupported,
  startVoiceNoteRecording,
  VoiceNoteError,
} from "../voiceNote";

export function useVoiceNoteRecorder({ chatId, onError, onReady } = {}) {
  const recordingRef = useRef(null);
  const generationRef = useRef(0);
  const [status, setStatus] = useState("idle");
  const [mediaStream, setMediaStream] = useState(null);

  const isActive =
    status === "requesting-mic" || status === "listening" || status === "transcribing";

  const cancel = useCallback(() => {
    generationRef.current += 1;
    recordingRef.current?.cancel();
    recordingRef.current = null;
    setMediaStream(null);
    setStatus("idle");
  }, []);

  useEffect(() => cancel, [cancel]);

  useEffect(() => {
    cancel();
  }, [chatId, cancel]);

  const start = useCallback(async (chatIdOverride = null) => {
    if (recordingRef.current || isActive) return false;
    if (!isVoiceNoteSupported()) {
      onError?.("Voice notes are not supported in this browser.");
      return false;
    }

    setStatus("requesting-mic");
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    try {
      const recording = await startVoiceNoteRecording({
        chatId: chatIdOverride || chatId,
        onAutoStop: () => {
          void stop();
        },
      });
      recordingRef.current = recording;
      if (generation !== generationRef.current) {
        recording.cancel();
        setMediaStream(null);
        return false;
      }
      setMediaStream(recording.stream || null);
      setStatus("listening");
      return true;
    } catch (error) {
      setStatus("idle");
      setMediaStream(null);
      onError?.(
        error instanceof Error ? error.message : "Could not start voice-note recording.",
        error,
      );
      return false;
    }
  }, [chatId, isActive, onError]);

  const stop = useCallback(async () => {
    const recording = recordingRef.current;
    if (!recording) return false;
    recordingRef.current = null;
    setMediaStream(null);
    const generation = generationRef.current;
    setStatus("transcribing");
    try {
      const result = await recording.stop();
      if (generation !== generationRef.current) return false;
      setStatus("idle");
      onReady?.(result);
      return true;
    } catch (error) {
      if (generation !== generationRef.current) return false;
      setStatus("idle");
      const message =
        error instanceof VoiceNoteError || error instanceof Error
          ? error.message
          : "Could not transcribe voice note.";
      onError?.(message, error);
      return false;
    }
  }, [onError, onReady]);

  return {
    status,
    mediaStream,
    isActive,
    isSupported: isVoiceNoteSupported(),
    start,
    stop,
    cancel,
  };
}
